import {hasSeoAttributes} from 'seo/seo-db/attributes'

import {logger} from '@vio/core/src/utils/logging/logger'

import type {FilterParameters} from '@findhotel/sapi'
import type {SeoAttributes} from 'seo/seo-db/attributes'

type FilterProcessor = {
  keys: (keyof FilterParameters)[]
  process: (
    result: Partial<FilterParameters>,
    key: keyof FilterParameters,
    value: string
  ) => void
}

type FilterKeysBoolean = keyof Pick<
  FilterParameters,
  | 'freeCancellation'
  | 'payLater'
  | 'exclusive'
  | 'noHostels'
  | 'mealIncluded'
  | 'dealFreeze' // Added as a part of c747927f-deal-freeze-srp
>

type FilterKeysNumber = keyof Pick<FilterParameters, 'priceMin' | 'priceMax'>

type FilterKeysArrayNumber = keyof Pick<
  FilterParameters,
  | 'facilities'
  | 'themeIds'
  | 'propertyTypeId'
  | 'chainIds'
  | 'starRating'
  | 'guestRating'
>

type FilterKeysArrayString = keyof Pick<
  FilterParameters,
  'brandIds' | 'neighborhoodIds' | 'amenities'
>

export const FRIENDLY_SRP_PATH_NAME = 'p'
export const FRIENDLY_SRP_FILTER_DIVIDER = '--'
export const FRIENDLY_SRP_FILTER_URL_DELIMITER = 'f'

const log = logger.child({
  module: 'getFiltersFromTaxonomy'
})

export const getFiltersFromTaxonomy = (
  taxonomy: string[] | string | undefined,
  seoAttributes: SeoAttributes
): Partial<FilterParameters> => {
  const filterSlugs = extractFilterSlugsFromTaxonomy(taxonomy)

  if (!filterSlugs.length || !hasSeoAttributes(seoAttributes)) {
    return {}
  }

  const result: Partial<FilterParameters> = {}

  for (const slug of filterSlugs) {
    const filterConfig = seoAttributes[slug]

    if (!filterConfig) {
      log.warn(`Unknown filter slug "${slug}"`, filterSlugs)
      continue
    }

    const {key, value} = filterConfig
    const processor = processors.find(p => p.keys.includes(key))

    if (!processor) {
      log.warn(`filter key "${key}" not processed`)
      continue
    }

    processor.process(result, key, value)
  }

  return result
}

const processBoolean = (
  result: Partial<FilterParameters>,
  key: FilterKeysBoolean,
  value: string
) => {
  if (value === '1') result[key] = true
}

const processNumber = (
  result: Partial<FilterParameters>,
  key: FilterKeysNumber,
  value: string
) => {
  const parsedValue = parseInt(value, 10)

  if (isNaN(parsedValue)) {
    log.warn(`Invalid number value "${key}" - "${value}"`)
    return
  }

  result[key] = parsedValue
}

const processArrayNumber = (
  result: Partial<FilterParameters>,
  key: FilterKeysArrayNumber,
  value: string
) => {
  const parsedValue = parseInt(value, 10)

  if (isNaN(parsedValue)) {
    log.warn(`Invalid number value "${key}" - "${value}"`)
    return
  }

  if (!result[key]) result[key] = []

  result[key].push(parsedValue)
}

const processArrayString = (
  result: Partial<FilterParameters>,
  key: FilterKeysArrayString,
  value: string
) => {
  if (!value) {
    log.warn(`Invalid string value "${key}" - "${value}"`)
    return
  }

  if (!result[key]) result[key] = []

  result[key].push(value)
}

const processors: FilterProcessor[] = [
  {
    keys: [
      'freeCancellation',
      'payLater',
      'exclusive',
      'noHostels',
      'mealIncluded',
      'dealFreeze' // Added as a part of c747927f-deal-freeze-srp
    ],
    process: processBoolean
  },
  {
    keys: ['priceMin', 'priceMax'],
    process: processNumber
  },
  {
    keys: [
      'facilities',
      'themeIds',
      'propertyTypeId',
      'chainIds',
      'starRating',
      'guestRating'
    ],
    process: processArrayNumber
  },
  {
    keys: ['brandIds', 'neighborhoodIds', 'amenities'],
    process: processArrayString
  }
]

export const extractFilterStringFromTaxonomy = (
  taxonomy: string[] | string | undefined
): string => {
  const hasFilters =
    taxonomy?.length && taxonomy.at(-2) === FRIENDLY_SRP_FILTER_URL_DELIMITER

  if (!hasFilters) return ''

  return taxonomy.at(-1)
}

export const extractFilterSlugsFromTaxonomy = (
  taxonomy: string[] | string | undefined
): string[] => {
  const filterString = extractFilterStringFromTaxonomy(taxonomy)

  return parseSeoAttributesString(filterString)
}

export const parseSeoAttributesString = (filterString: string): string[] => {
  if (!filterString) return []

  const filterSlugs = new Set<string>(
    filterString
      .split(FRIENDLY_SRP_FILTER_DIVIDER)
      .filter(Boolean)
      .map(attr => attr.trim())
  )
  return Array.from(filterSlugs)
}
